import type {
  Grid,
  GridPosition,
  MoneyCents,
  Payline,
  ReelStops,
  RegularSymbolId,
} from '../core/types';
import type { EgyptianGodEvent, PersistedStickyWild } from '../bonus/types';

export const REPLAY_SCHEMA_VERSION = 2 as const;
/** Technical corruption guard, not a gameplay cap. Extreme/Ra may extend features without a design cap. */
export const MAX_REPLAY_STEPS = 10_000;

export type ReplayPresentationMode = 'cards' | 'performance';

export interface ReplayLineWinV2 {
  readonly lineIndex: number;
  readonly route: Payline;
  readonly count: 3 | 4 | 5;
  readonly symbolId: RegularSymbolId | 'WILD';
  readonly winCents: MoneyCents;
}

export interface ReplayPotOfGreedV2 {
  readonly position: GridPosition;
  readonly awardedFreeSpins: 1;
  readonly transformedToWild: true;
}

export interface ReplayStepV2 {
  readonly spinIndex: number;
  readonly stops: ReelStops;
  readonly grid: Grid;
  readonly lineWins: readonly ReplayLineWinV2[];
  readonly spinWinCents: MoneyCents;
  readonly cumulativeWinCents: MoneyCents;
  readonly scatterCount: number;
  readonly retriggerAwarded: number;
  readonly freeSpinsLeftAfter: number;
  readonly freeSpinsTotalAfter: number;
  readonly stickyBefore: readonly PersistedStickyWild[];
  readonly stickyAfter: readonly PersistedStickyWild[];
  readonly newlySticky: readonly PersistedStickyWild[];
  readonly potOfGreed?: ReplayPotOfGreedV2;
  readonly godEvents?: readonly EgyptianGodEvent[];
}

export interface FreeSpinReplayV2 {
  readonly version: typeof REPLAY_SCHEMA_VERSION;
  readonly id: string;
  readonly clientEntryId: string;
  readonly ownerUid?: string;
  readonly name: string;
  readonly betCents: MoneyCents;
  readonly totalWinCents: MoneyCents;
  readonly createdAt: number;
  readonly presentationMode: ReplayPresentationMode;
  readonly totalSpins: number;
  readonly totalAwarded: number;
  readonly retriggerCount: number;
  readonly finalStickyCount: number;
  readonly steps: readonly ReplayStepV2[];
  readonly legacySource?: true;
  readonly linesComplete: boolean;
}

export interface ReplayDraftV2 {
  readonly version: typeof REPLAY_SCHEMA_VERSION;
  readonly id: string;
  readonly name: string;
  readonly betCents: MoneyCents;
  readonly startedAt: number;
  readonly presentationMode: ReplayPresentationMode;
  readonly totalAwarded: number;
  readonly retriggerCount: number;
  readonly steps: readonly ReplayStepV2[];
}

/** Optional one-time adapter for legacy v1 frames, which did not persist line wins. */
export interface LegacyReplayLineDecoder {
  decodeStoredResult(input: Readonly<{
    grid: Grid;
    betCents: MoneyCents;
    spinWinCents: MoneyCents;
    /** Sticky wilds already active before this legacy free-spin is evaluated. */
    stickyPositions?: readonly GridPosition[];
  }>): readonly ReplayLineWinV2[] | null;
}
